import { Box, TextField } from '@mui/material';
import type { ITextInputData } from '@sharedInterfaces/IEmployeeTalkState';
import type { ITextInputComponentProperties } from '@sharedInterfaces/IEmployeeTalkTemplate';
import { getLanguageTexts } from '@sharedInterfaces/Language/languageHelper';
import type { AppState } from '@store/store';
import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';

import type { ITemplateComponentProps } from '../TemplateEditor/types';
import ETTBaseTemplateComponent from './ETTBaseTemplateComponent';
import { useEmployeeTalk } from './ETTEmployeeTalkProvider';

export interface IETTTextInputComponentProps extends ITemplateComponentProps {
    // No longer need value prop - data comes from provider
}

export const ETTTextInputComponent: React.FC<IETTTextInputComponentProps> = (props) => {
    const { component } = props;
    const properties = component.properties as ITextInputComponentProperties;
    const placeholder = properties.placeholder || '';
    const maxLength = properties.maxLength || 255;

    // Use event-driven state management
    const { getComponentData, updateComponentData, isReadOnlyForCurrentUser } = useEmployeeTalk();
    const componentData = getComponentData<ITextInputData>(component.id);
    const [inputValue, setInputValue] = React.useState(componentData?.text || properties.defaultValue || '');

    const lang = useSelector((state: AppState) => state.employee.language);
    const langStrings = getLanguageTexts(lang).employeeTalk;

    // Update local state when component data changes
    useEffect(() => {
        if (componentData?.text !== undefined) {
            setInputValue(componentData.text);
        }
    }, [componentData]);

    const handleChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = e.target.value;
        setInputValue(newValue);

        // Update through event system
        await updateComponentData(component.id, { text: newValue });
    };

    return (
        <ETTBaseTemplateComponent
            {...props}
            renderEditContent={() => (
                <Box>
                    <TextField
                        fullWidth
                        variant="outlined"
                        label={langStrings.placeholder}
                        value={placeholder}
                        onChange={(e) => {
                            if (props.onEdit) {
                                props.onEdit({
                                    ...component,
                                    properties: {
                                        ...properties,
                                        placeholder: e.target.value,
                                    } as ITextInputComponentProperties,
                                });
                            }
                        }}
                        sx={{ mb: 2 }}
                    />
                    <TextField
                        fullWidth
                        variant="outlined"
                        label={langStrings.maxLength}
                        type="number"
                        value={maxLength}
                        onChange={(e) => {
                            if (props.onEdit) {
                                props.onEdit({
                                    ...component,
                                    properties: {
                                        ...properties,
                                        maxLength: parseInt(e.target.value) || 255,
                                    } as ITextInputComponentProperties,
                                });
                            }
                        }}
                    />
                </Box>
            )}
            renderViewContent={() => (
                <TextField
                    fullWidth
                    variant="outlined"
                    placeholder={placeholder}
                    inputProps={{ maxLength }}
                    value={inputValue}
                    onChange={handleChange}
                    disabled={isReadOnlyForCurrentUser}
                />
            )}
        />
    );
};

export default ETTTextInputComponent;
